import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPin,
  ArrowLeft,
  Package,
  AlertTriangle,
  ArrowRightLeft,
  TrendingUp,
} from 'lucide-react';
import Card from '../common/Card';
import Table from '../common/Table';
import StatusIndicator, { getStockLevel, getStockLevelColor } from '../common/StatusIndicator';
import VolumeChart from '../Charts/VolumeChart';
import { useApi } from '../../hooks/useApi';
import { useSocketContext } from '../../App';
import {
  getZoneDetail,
  getStockByZone,
  getTransactions,
  getZoneTransfers,
} from '../../api/client';
import { format } from 'date-fns';

const stockColumns = [
  { key: 'classname', label: 'Item', render: (v) => <span className="font-medium text-xs">{v}</span> },
  { key: 'category', label: 'Category', render: (v) => <span className="text-text-secondary text-xs">{v}</span> },
  {
    key: 'quantity',
    label: 'Stock',
    mono: true,
    align: 'right',
    render: (v) => <span className="font-mono text-xs">{(v || 0).toLocaleString()}</span>,
  },
  {
    key: 'maxQuantity',
    label: 'Max',
    mono: true,
    align: 'right',
    render: (v, row) => <span className="font-mono text-text-muted text-xs">{(v || row.max_quantity || 0).toLocaleString()}</span>,
  },
  {
    key: 'percent',
    label: 'Level',
    align: 'right',
    render: (v, row) => {
      const pct = v ?? (row.quantity && row.maxQuantity ? (row.quantity / row.maxQuantity) * 100 : 0);
      return <StatusIndicator type="stock" value={pct} />;
    },
  },
];

const txColumns = [
  {
    key: 'type',
    label: 'Type',
    render: (v) => <StatusIndicator type="transaction" value={v} />,
  },
  { key: 'classname', label: 'Item', render: (v) => <span className="text-xs font-medium">{v}</span> },
  { key: 'quantity', label: 'Qty', mono: true, align: 'right', render: (v) => <span className="font-mono text-xs">{v}</span> },
  {
    key: 'price',
    label: 'Price',
    mono: true,
    align: 'right',
    render: (v) => <span className="font-mono text-accent-green text-xs">₽{(v || 0).toLocaleString()}</span>,
  },
  {
    key: 'timestamp',
    label: 'Time',
    render: (v) => (
      <span className="text-text-muted text-xs font-mono">
        {v ? format(new Date(v), 'HH:mm:ss') : '—'}
      </span>
    ),
  },
];

const transferColumns = [
  { key: 'fromZone', label: 'From', render: (v, row) => <span className="text-xs">{v || row.from_zone || '—'}</span> },
  { key: 'toZone', label: 'To', render: (v, row) => <span className="text-xs">{v || row.to_zone || '—'}</span> },
  { key: 'classname', label: 'Item', render: (v) => <span className="text-xs font-medium">{v}</span> },
  { key: 'quantity', label: 'Qty', mono: true, align: 'right', render: (v) => <span className="font-mono text-xs">{v}</span> },
  {
    key: 'timestamp',
    label: 'Time',
    render: (v) => (
      <span className="text-text-muted text-xs font-mono">
        {v ? format(new Date(v), 'MMM dd HH:mm') : '—'}
      </span>
    ),
  },
];

export default function ZoneDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const socket = useSocketContext();

  const { data: zoneInfo, loading: zoneLoading } = useApi(getZoneDetail, name);
  const { data: stockData, loading: stockLoading, refetch: refetchStock } = useApi(getStockByZone, name);
  const { data: txData, loading: txLoading } = useApi(getTransactions, { zone: name, limit: 50 });
  const { data: transfers, loading: transfersLoading } = useApi(getZoneTransfers, name);

  // Subscribe to zone updates
  useEffect(() => {
    if (socket?.subscribeToZone && name) {
      socket.subscribeToZone(name);
      return () => socket.unsubscribeFromZone(name);
    }
  }, [socket, name]);

  // Refetch on stock updates
  useEffect(() => {
    if (socket?.stockUpdates?.length > 0) {
      const latestUpdate = socket.stockUpdates[0];
      if (latestUpdate?.zone === name) {
        refetchStock();
      }
    }
  }, [socket?.stockUpdates, name, refetchStock]);

  const stockItems = useMemo(() => {
    if (!stockData) return [];
    const items = Array.isArray(stockData) ? stockData : stockData.items || stockData.data || [];
    return items.map((item) => ({
      ...item,
      percent: item.percent ?? (item.quantity && (item.maxQuantity || item.max_quantity)
        ? (item.quantity / (item.maxQuantity || item.max_quantity)) * 100
        : 0),
    }));
  }, [stockData]);

  const recentTxns = useMemo(() => {
    if (!txData) return [];
    return Array.isArray(txData) ? txData : txData.data || [];
  }, [txData]);

  const transferList = useMemo(() => {
    if (!transfers) return [];
    return Array.isArray(transfers) ? transfers : transfers.data || [];
  }, [transfers]);

  const lowStockItems = stockItems.filter((i) => i.percent < 20);
  const overallStock = stockItems.length > 0
    ? stockItems.reduce((sum, i) => sum + i.percent, 0) / stockItems.length
    : 0;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/zones')}
          className="p-2 rounded bg-terminal-surface border border-terminal-border hover:border-text-muted transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-text-secondary" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
            <MapPin className="w-5 h-5 text-accent-green" />
            {name}
          </h1>
          <p className="text-xs text-text-muted font-mono">
            {zoneInfo?.description || 'Trader zone'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-[10px] text-text-muted uppercase">Stock Level</p>
            <p className="text-lg font-mono font-bold" style={{ color: getStockLevelColor(overallStock) }}>
              {overallStock.toFixed(1)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-text-muted uppercase">Items</p>
            <p className="text-lg font-mono font-bold text-text-primary">{stockItems.length}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-text-muted uppercase">Alerts</p>
            <p className={`text-lg font-mono font-bold ${lowStockItems.length > 0 ? 'text-accent-amber' : 'text-text-muted'}`}>
              {lowStockItems.length}
            </p>
          </div>
        </div>
      </div>

      {/* Low Stock Alerts */}
      {lowStockItems.length > 0 && (
        <Card glow="red" className="border-accent-amber/30">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-accent-amber" />
            <span className="text-xs font-medium text-accent-amber">
              {lowStockItems.length} items below stock threshold
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStockItems.slice(0, 10).map((item) => (
              <span
                key={item.classname || item.name}
                className="px-2 py-1 bg-accent-amber/10 border border-accent-amber/20 rounded text-[11px] font-mono text-accent-amber"
              >
                {item.classname || item.name} ({item.percent?.toFixed(0)}%)
              </span>
            ))}
            {lowStockItems.length > 10 && (
              <span className="text-[11px] text-text-muted font-mono">
                +{lowStockItems.length - 10} more
              </span>
            )}
          </div>
        </Card>
      )}

      {/* Stock Table + Volume Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Stock Inventory" icon={Package}>
          <Table
            columns={stockColumns}
            data={stockItems}
            loading={stockLoading}
            pageSize={12}
            onRowClick={(row) => row.classname && navigate(`/items/${row.classname}`)}
            emptyMessage="No stock data available"
          />
        </Card>

        <VolumeChart zone={name} className="h-full" />
      </div>

      {/* Transactions + Transfers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Recent Transactions" icon={TrendingUp}>
          <Table
            columns={txColumns}
            data={recentTxns}
            loading={txLoading}
            pageSize={10}
            onRowClick={(row) => row.classname && navigate(`/items/${row.classname}`)}
            emptyMessage="No transactions found"
          />
        </Card>

        <Card title="Zone Transfers" icon={ArrowRightLeft}>
          <Table
            columns={transferColumns}
            data={transferList}
            loading={transfersLoading}
            pageSize={10}
            emptyMessage="No transfers found"
          />
        </Card>
      </div>
    </div>
  );
}
